/**
 * 읽기 전용 MCP 도구 구현(§6.4). 모두 순수 함수다: 스냅샷 + 인자 → JSON.
 * 숫자는 화면과 **같은 계산**(`buildReportContext`)에서 온다. 화면과 MCP가 다른 값을 내면 P0 결함이다(ADV-E10).
 */
import type { Project, ScheduledTask, UserResource } from "@/engine/types";
import { buildReportContext, reportChecks, reportLevel, scheduleHeadline, type ReportContext, type ReportMetrics } from "@/features/reports/report-model";
import { clip, fit, page, readLimit, ToolFailure, tokensMatch } from "./paging";
import { findProject, type McpSnapshot, type McpWorkspace } from "./snapshot";

export interface ToolOptions {
  /** 오늘(YYYY-MM-DD). 이슈 기한 판정 등에 쓴다. 테스트는 고정값을 준다. */
  today: string;
}

type Args = Record<string, unknown>;

const ctxCache = new WeakMap<Project, { resources: Record<string, UserResource>; ctx: ReportContext }>();

/** 현황 화면과 같은 계산. 같은 프로젝트 객체면 다시 계산하지 않는다. */
export function contextOf(project: Project, resources: Record<string, UserResource>): ReportContext {
  const hit = ctxCache.get(project);
  if (hit && hit.resources === resources) return hit.ctx;
  const ctx = buildReportContext(project, resources);
  ctxCache.set(project, { resources, ctx });
  return ctx;
}

function str(args: Args, key: string): string | undefined {
  const v = args[key];
  if (v === undefined) return undefined;
  if (typeof v !== "string") throw new ToolFailure("INVALID_ARGUMENT", `${key}: 문자열이어야 합니다`);
  return v;
}

function requireProject(snap: McpSnapshot, args: Args) {
  const id = str(args, "projectId");
  if (!id) throw new ToolFailure("INVALID_ARGUMENT", "projectId: 필수 필드입니다");
  const hit = findProject(snap, id);
  if (!hit) throw new ToolFailure("NOT_FOUND", "플랜을 찾지 못했습니다");
  return hit;
}

function workspaceOf(snap: McpSnapshot, id: string | undefined): McpWorkspace {
  const ws = snap.workspaces.find((w) => w.id === (id ?? snap.activeWorkspaceId)) ?? (id ? undefined : snap.workspaces[0]);
  if (!ws) throw new ToolFailure("NOT_FOUND", "워크스페이스를 찾지 못했습니다");
  return ws;
}

export function projectStatus(m: ReportMetrics): "notStarted" | "inProgress" | "done" {
  if (m.leafCount > 0 && m.progress >= 100) return "done";
  if (m.progress > 0 || m.statusDate >= m.projectStart) return "inProgress";
  return "notStarted";
}

function progressState(pct: number): "notStarted" | "inProgress" | "done" {
  return pct >= 100 ? "done" : pct > 0 ? "inProgress" : "notStarted";
}

function scheduleSummary(ctx: ReportContext) {
  const m = ctx.metrics;
  const head = scheduleHeadline(ctx);
  const level = reportLevel(m);
  return {
    statusDate: m.statusDate,
    start: m.projectStart,
    finish: head.projectFinish,
    durationDays: m.projectDuration,
    progressPct: head.progress,
    plannedProgressPct: m.plannedProgress,
    spi: head.spi === null ? null : Math.round(head.spi * 100) / 100,
    health: { score: m.healthScore, level: level.level, reasons: level.reasons.map((r) => clip(r, 300)) },
    criticalPathLength: m.criticalCount,
    delayCount: head.delayCount,
    baseline: head.hasBaseline ? { name: clip(m.baselineName ?? "", 200), finish: ctx.evm.baselineFinish ?? null, finishVarianceDays: head.finishVarianceDays } : null,
    milestones: { total: m.milestoneCount, done: m.milestoneDone },
    scheduleErrors: ctx.schedule.errors.length,
  };
}

function taskRow(project: Project, st: ScheduledTask, resources: Record<string, UserResource>) {
  const t = project.tasks.find((x) => x.id === st.id);
  const assignee = t?.assignee ?? (t?.assignedResourceId ? resources[t.assignedResourceId]?.displayName : undefined);
  return {
    id: st.id,
    wbs: st.wbs,
    name: clip(t?.name ?? ""),
    parentId: t?.parentId ?? null,
    start: st.start,
    finish: st.finish,
    durationDays: st.duration,
    progressPct: st.percentComplete,
    status: progressState(st.percentComplete),
    assignee: clip(assignee ?? null, 200) ?? null,
    critical: st.critical,
    totalFloat: st.totalFloat,
    isSummary: st.isSummary,
    isMilestone: st.isMilestone,
    deadline: t?.deadline ?? null,
    deadlineMissed: st.deadlineMissed,
  };
}

export const tools = {
  list_workspaces(snap: McpSnapshot, args: Args) {
    return page(snap.workspaces, args, (ws) => ({ id: ws.id, name: clip(ws.name, 200), projectCount: ws.projects.length, active: ws.id === snap.activeWorkspaceId }));
  },

  list_projects(snap: McpSnapshot, args: Args) {
    const wsId = str(args, "workspaceId");
    const query = str(args, "query")?.trim();
    const spaces = wsId ? [workspaceOf(snap, wsId)] : snap.workspaces;
    const rows = spaces.flatMap((ws) => ws.projects.map((p) => ({ ws, p }))).filter(({ p }) => !query || tokensMatch(query, p.name));
    return page(rows, args, ({ ws, p }) => {
      const m = contextOf(p, ws.resources).metrics;
      return {
        id: p.id,
        workspaceId: ws.id,
        name: clip(p.name),
        status: projectStatus(m),
        start: m.projectStart,
        finish: m.projectFinish,
        statusDate: m.statusDate,
        progressPct: m.progress,
        updatedAt: p.updatedAt,
      };
    });
  },

  get_project(snap: McpSnapshot, args: Args) {
    const { ws, project } = requireProject(snap, args);
    const ctx = contextOf(project, ws.resources);
    return {
      id: project.id,
      workspaceId: ws.id,
      workspaceName: clip(ws.name, 200),
      name: clip(project.name),
      start: project.start,
      statusDate: project.statusDate,
      updatedAt: project.updatedAt,
      taskCount: project.tasks.length,
      executionTaskCount: project.executionTasks.length,
      recordCount: project.records.length,
      activeBaseline: ctx.baseline ? { id: ctx.baseline.id, name: clip(ctx.baseline.name, 200), createdAt: ctx.baseline.createdAt } : null,
      schedule: scheduleSummary(ctx),
      health: {
        score: ctx.health.score,
        counts: ctx.health.counts,
        issues: ctx.health.issues.slice(0, 20).map((i) => ({ code: i.code, severity: i.severity, title: clip(i.title, 300), detail: clip(i.detail, 500), taskIds: i.taskIds.slice(0, 20) })),
        issueCount: ctx.health.issues.length,
      },
    };
  },

  list_tasks(snap: McpSnapshot, args: Args) {
    const { ws, project } = requireProject(snap, args);
    const ctx = contextOf(project, ws.resources);
    const status = str(args, "status");
    const assignee = str(args, "assignee")?.trim().toLowerCase();
    const from = str(args, "from");
    const to = str(args, "to");
    const criticalOnly = args.criticalOnly === true;
    const byId = new Map(project.tasks.map((t) => [t.id, t]));
    const rows = ctx.schedule.order
      .map((id) => ctx.schedule.tasks.get(id)!)
      .filter((st) => {
        if (status && progressState(st.percentComplete) !== status) return false;
        if (criticalOnly && !st.critical) return false;
        if (from && st.finish < from) return false;
        if (to && st.start > to) return false;
        if (assignee) {
          const t = byId.get(st.id);
          const names = [t?.assignee, t?.assignedResourceId ? ws.resources[t.assignedResourceId]?.displayName : undefined].filter(Boolean).join(" ").toLowerCase();
          if (!names.includes(assignee)) return false;
        }
        return true;
      });
    return { projectId: project.id, ...page(rows, args, (st) => taskRow(project, st, ws.resources)) };
  },

  get_task(snap: McpSnapshot, args: Args) {
    const { ws, project } = requireProject(snap, args);
    const taskId = str(args, "taskId");
    const task = project.tasks.find((t) => t.id === taskId);
    if (!task) throw new ToolFailure("NOT_FOUND", "작업을 찾지 못했습니다");
    const ctx = contextOf(project, ws.resources);
    const st = ctx.schedule.tasks.get(task.id)!;
    const ref = (id: string) => ({ taskId: id, wbs: ctx.schedule.tasks.get(id)?.wbs ?? null, name: clip(project.tasks.find((t) => t.id === id)?.name ?? "", 300) });
    const preds = project.dependencies.filter((d) => d.successorId === task.id).map((d) => ({ ...ref(d.predecessorId), type: d.type, lag: d.lag }));
    const succs = project.dependencies.filter((d) => d.predecessorId === task.id).map((d) => ({ ...ref(d.successorId), type: d.type, lag: d.lag }));
    const bt = ctx.baseline?.tasks[task.id];
    const linked = project.executionTasks.filter((x) => x.linkedTaskIds.includes(task.id));
    const resource = task.assignedResourceId ? ws.resources[task.assignedResourceId] : undefined;
    return {
      projectId: project.id,
      ...taskRow(project, st, ws.resources),
      freeFloat: st.freeFloat,
      constraint: task.constraint ? { type: task.constraint.type, date: task.constraint.date } : null,
      constraintConflict: st.constraintConflict,
      driver: st.driver,
      progressMethod: task.progressMethod,
      plannedMM: task.plannedMM ?? null,
      resource: resource ? { id: resource.id, name: clip(resource.displayName, 200), roleTitle: clip(resource.roleTitle, 200) } : null,
      deliverable: clip(task.deliverable) ?? null,
      acceptance: clip(task.acceptance) ?? null,
      note: clip(task.note) ?? null,
      predecessors: preds.slice(0, 100),
      successors: succs.slice(0, 100),
      baseline: bt
        ? { start: bt.start, finish: bt.finish, durationDays: bt.duration, finishVarianceDays: ctx.calendar.ordinalBeforeISO(st.finish) - ctx.calendar.ordinalBeforeISO(bt.finish) }
        : null,
      linkedExecutionTasks: {
        total: linked.length,
        byStatus: linked.reduce<Record<string, number>>((acc, x) => ((acc[x.status ?? "notStarted"] = (acc[x.status ?? "notStarted"] ?? 0) + 1), acc), {}),
        items: linked.slice(0, 20).map((x) => ({ id: x.id, title: clip(x.title, 300), status: x.status ?? "notStarted", due: x.due ?? null, assignees: x.assignees.slice(0, 10) })),
      },
    };
  },

  get_schedule_summary(snap: McpSnapshot, args: Args) {
    const { ws, project } = requireProject(snap, args);
    return { projectId: project.id, name: clip(project.name), ...scheduleSummary(contextOf(project, ws.resources)) };
  },

  get_critical_path(snap: McpSnapshot, args: Args) {
    const { ws, project } = requireProject(snap, args);
    const ctx = contextOf(project, ws.resources);
    const rows = ctx.schedule.criticalPath.map((id) => ctx.schedule.tasks.get(id)!);
    const listed = fit(rows, (st) => ({
      id: st.id,
      wbs: st.wbs,
      name: clip(project.tasks.find((t) => t.id === st.id)?.name ?? ""),
      start: st.start,
      finish: st.finish,
      durationDays: st.duration,
      totalFloat: st.totalFloat,
      progressPct: st.percentComplete,
    }));
    return {
      projectId: project.id,
      projectFinish: ctx.schedule.projectFinish,
      criticalThreshold: project.criticalThreshold,
      finishImpact: `아래 작업은 여유가 ${project.criticalThreshold}일 이하입니다. 이 작업이 늦어지면 예상 종료일도 그만큼 늦어집니다.`,
      total: rows.length,
      items: listed.items,
      truncated: listed.truncated,
    };
  },

  list_resources(snap: McpSnapshot, args: Args) {
    const projectId = str(args, "projectId");
    const teamName = (ws: McpWorkspace, id?: string) => (id ? clip(ws.teams[id]?.name ?? null, 200) ?? null : null);
    if (projectId) {
      const { ws, project } = requireProject(snap, args);
      const rows = project.resourceAssignments;
      return {
        projectId: project.id,
        workspaceId: ws.id,
        ...page(rows, args, (a) => {
          const r = ws.resources[a.resourceId];
          return {
            resourceId: a.resourceId,
            name: clip(r?.displayName ?? "알 수 없는 인력", 200),
            roleTitle: clip(r?.roleTitle ?? null, 200) ?? null,
            team: teamName(ws, r?.teamId),
            projectRole: clip(a.projectRole, 200),
            allocationPct: Math.round(a.allocationRatio * 100),
            plannedMM: a.plannedMM,
            startDate: a.startDate,
            endDate: a.endDate,
          };
        }),
      };
    }
    const ws = workspaceOf(snap, str(args, "workspaceId"));
    const rows = Object.values(ws.resources);
    return {
      workspaceId: ws.id,
      teams: Object.values(ws.teams).map((t) => ({ id: t.id, name: clip(t.name, 200), memberCount: rows.filter((r) => r.teamId === t.id).length })),
      ...page(rows, args, (r) => {
        // 리소스 화면과 같은 계산(catalog-store getResourceWorkload): 워크스페이스의 모든 배정 합계
        let ratio = 0;
        let mm = 0;
        let projects = 0;
        for (const p of ws.projects) {
          for (const a of p.resourceAssignments ?? []) {
            if (a.resourceId !== r.id) continue;
            ratio += a.allocationRatio;
            mm += a.plannedMM;
            projects++;
          }
        }
        return {
          id: r.id,
          name: clip(r.displayName, 200),
          roleTitle: clip(r.roleTitle, 200),
          team: teamName(ws, r.teamId),
          isActive: r.isActive,
          totalAllocationPct: Math.round(ratio * 100),
          plannedMM: Math.round(mm * 10) / 10,
          assignmentCount: projects,
          overAllocated: ratio > 1.05,
        };
      }),
    };
  },

  get_report(snap: McpSnapshot, args: Args, opts: ToolOptions) {
    const { ws, project } = requireProject(snap, args);
    const periodId = str(args, "period");
    const periods = ws.reports[project.id] ?? [];
    let ctx: ReportContext;
    let recordDay: string;
    let period: { id: string; name: string | undefined; closedAt: string } | "current";
    if (!periodId || periodId === "current") {
      ctx = contextOf(project, ws.resources);
      recordDay = opts.today;
      period = "current";
    } else {
      const p = periods.find((x) => x.id === periodId);
      if (!p) throw new ToolFailure("NOT_FOUND", "보고 기간을 찾지 못했습니다");
      // 마감한 기간은 화면과 같이 마감 때 지표(비용)를 고정해 다시 계산한다
      ctx = buildReportContext(p.project, ws.resources, p.metrics);
      recordDay = ctx.metrics.statusDate;
      period = { id: p.id, name: clip(p.name, 200), closedAt: p.closedAt };
    }
    const level = reportLevel(ctx.metrics);
    return {
      projectId: project.id,
      period,
      metrics: ctx.metrics,
      level: { level: level.level, reasons: level.reasons.map((r) => clip(r, 300)) },
      checks: reportChecks(ctx, recordDay).map((c) => ({ key: c.key, label: c.label, status: c.status, figure: clip(c.figure, 300), aside: clip(c.aside, 300) ?? null, text: clip(c.text, 500), rule: clip(c.rule, 300) ?? null })),
      delays: ctx.delays.slice(0, 20).map((d) => ({ taskId: d.taskId, wbs: d.wbs, name: clip(d.name, 300), baselineFinish: d.baselineFinish, finish: d.finish, varianceDays: d.varianceDays, critical: d.critical })),
      periods: periods.slice(0, 52).map((p) => ({ id: p.id, name: clip(p.name, 200), closedAt: p.closedAt })),
    };
  },

  search(snap: McpSnapshot, args: Args) {
    const query = str(args, "query")?.trim() ?? "";
    if (!query) throw new ToolFailure("INVALID_ARGUMENT", "query: 비어 있을 수 없습니다");
    const scope = str(args, "scope") ?? "all";
    const limit = readLimit(args);
    type Hit =
      | { kind: "project"; workspaceId: string; projectId: string; name: string | undefined }
      | { kind: "task"; workspaceId: string; projectId: string; taskId: string; wbs: string; name: string | undefined; assignee: string | null };
    const hits: Hit[] = [];
    // 결과 수 상한(limit)+1까지만 모아 더 있는지 알 수 있게 한다. 작업이 많은 워크스페이스에서도 전체를 훑지 않는다.
    outer: for (const ws of snap.workspaces) {
      for (const p of ws.projects) {
        if (scope !== "tasks" && tokensMatch(query, p.name)) {
          hits.push({ kind: "project", workspaceId: ws.id, projectId: p.id, name: clip(p.name) });
          if (hits.length > limit) break outer;
        }
        if (scope === "projects") continue;
        const sched = contextOf(p, ws.resources).schedule;
        for (const t of p.tasks) {
          const wbs = sched.tasks.get(t.id)?.wbs ?? "";
          if (!tokensMatch(query, `${wbs} ${t.name} ${t.assignee ?? ""}`)) continue;
          hits.push({ kind: "task", workspaceId: ws.id, projectId: p.id, taskId: t.id, wbs, name: clip(t.name), assignee: clip(t.assignee ?? null, 200) ?? null });
          if (hits.length > limit) break outer;
        }
      }
    }
    const listed = fit(hits, (h) => h, limit);
    return { query: clip(query, 200), items: listed.items, truncated: listed.truncated };
  },
} satisfies Record<string, (snap: McpSnapshot, args: Args, opts: ToolOptions) => unknown>;

export type ToolName = keyof typeof tools;
